import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { InvoiceDisplayStatusV2, PaymentDetailType } from 'anchor-scout-common/types';
const SPARKLINE_STATUS_BUCKET_IDS = [
    'paid',
    'pending',
    'overdue'
];
const SPARKLINE_STATUS_BUCKETS = new Set(SPARKLINE_STATUS_BUCKET_IDS);
export const isSparklineStatusBucket = (id)=>SPARKLINE_STATUS_BUCKETS.has(id);
const NUMBER_ONLY_STATUS_BUCKET_IDS = [
    'issued',
    'processing',
    'refunded',
    'voided'
];
const NUMBER_ONLY_STATUS_BUCKETS = new Set(NUMBER_ONLY_STATUS_BUCKET_IDS);
export const isNumberOnlyStatusBucket = (id)=>NUMBER_ONLY_STATUS_BUCKETS.has(id);
export const CARD_LOCALE_KEY = {
    'collection-rate': 'collectionRate',
    'anchor-vs-external': 'anchorVsExternal',
    'avg-days-to-pay': 'avgDaysToPay',
    'avg-invoice': 'avgInvoice',
    outstanding: 'outstanding',
    'aging-breakdown': 'agingBreakdown',
    pending: 'pending',
    issued: 'issued',
    processing: 'processing',
    paid: 'paid',
    overdue: 'overdue',
    voided: 'voided',
    refunded: 'refunded'
};
const compactCurrencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumFractionDigits: 1
});
const wholeCurrencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
});
const fullCurrencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
});
const formatFullOrWhole = (amount)=>Number.isInteger(amount) ? wholeCurrencyFormatter.format(amount) : fullCurrencyFormatter.format(amount);
export const formatCompactCurrency = (amount)=>Math.abs(amount) >= 1000000 ? compactCurrencyFormatter.format(amount) : formatFullOrWhole(amount);
export const formatCurrency = (amount)=>formatFullOrWhole(amount);
export const formatPercent = (ratio)=>"".concat(Math.round(ratio * 100), "%");
const OPEN_STATUSES = [
    InvoiceDisplayStatusV2.Issued,
    InvoiceDisplayStatusV2.Pending,
    InvoiceDisplayStatusV2.Overdue,
    InvoiceDisplayStatusV2.Processing
];
const PAID_ONLY = [
    InvoiceDisplayStatusV2.Paid
];
const CARD_FILTERS = {
    'collection-rate': {
        displayStatusV2: PAID_ONLY
    },
    'anchor-vs-external': {
        displayStatusV2: PAID_ONLY
    },
    'avg-days-to-pay': {
        displayStatusV2: PAID_ONLY
    },
    'avg-invoice': {
        displayStatusV2: []
    },
    outstanding: {
        displayStatusV2: OPEN_STATUSES
    },
    'aging-breakdown': {
        displayStatusV2: OPEN_STATUSES
    },
    paid: {
        displayStatusV2: PAID_ONLY
    },
    pending: {
        displayStatusV2: [
            InvoiceDisplayStatusV2.Pending
        ]
    },
    overdue: {
        displayStatusV2: [
            InvoiceDisplayStatusV2.Overdue
        ]
    },
    issued: {
        displayStatusV2: [
            InvoiceDisplayStatusV2.Issued
        ]
    },
    processing: {
        displayStatusV2: [
            InvoiceDisplayStatusV2.Processing
        ]
    },
    voided: {
        displayStatusV2: [
            InvoiceDisplayStatusV2.Voided
        ]
    },
    refunded: {
        displayStatusV2: [
            InvoiceDisplayStatusV2.Refunded,
            InvoiceDisplayStatusV2.PartiallyRefunded
        ]
    }
};
export const filterForCard = (cardId)=>CARD_FILTERS[cardId];
export const subtractCardFilter = (currentFilters, delta)=>{
    var _delta_paymentDetails;
    if (delta == null) return currentFilters;
    const removedStatuses = new Set(delta.displayStatusV2);
    const removedPaymentDetails = new Set((_delta_paymentDetails = delta.paymentDetails) !== null && _delta_paymentDetails !== void 0 ? _delta_paymentDetails : []);
    return {
        displayStatusV2: currentFilters.displayStatusV2.filter((s)=>!removedStatuses.has(s)),
        paymentDetails: currentFilters.paymentDetails.filter((p)=>!removedPaymentDetails.has(p)),
        search: currentFilters.search,
        dueDateSince: delta.dueDateSince != null && delta.dueDateSince === currentFilters.dueDateSince ? undefined : currentFilters.dueDateSince,
        dueDateUntil: delta.dueDateUntil != null && delta.dueDateUntil === currentFilters.dueDateUntil ? undefined : currentFilters.dueDateUntil
    };
};
export const filterForOutstandingSegment = (segmentId)=>{
    var _map_segmentId;
    const map = {
        issued: {
            displayStatusV2: [
                InvoiceDisplayStatusV2.Issued
            ]
        },
        processing: {
            displayStatusV2: [
                InvoiceDisplayStatusV2.Processing
            ]
        },
        pending: {
            displayStatusV2: [
                InvoiceDisplayStatusV2.Pending
            ]
        },
        overdue: {
            displayStatusV2: [
                InvoiceDisplayStatusV2.Overdue
            ]
        }
    };
    return (_map_segmentId = map[segmentId]) !== null && _map_segmentId !== void 0 ? _map_segmentId : null;
};
const DAY_MS = 24 * 60 * 60 * 1000;
const startOfUtcDay = (timestampMs)=>{
    const d = new Date(timestampMs);
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};
const endOfUtcDay = (timestampMs)=>startOfUtcDay(timestampMs) + DAY_MS - 1;
export const filterForAgingSegment = (segmentId, now)=>{
    if (segmentId === 'current') {
        return {
            displayStatusV2: [
                InvoiceDisplayStatusV2.Issued,
                InvoiceDisplayStatusV2.Processing
            ]
        };
    }
    const overdue = {
        displayStatusV2: [
            InvoiceDisplayStatusV2.Overdue
        ]
    };
    const nowMs = now.getTime();
    // \"N days overdue\" means dueDate is N days before today. Buckets are inclusive on both ends.
    if (segmentId === 'days1to30') {
        return _object_spread_props(_object_spread({}, overdue), {
            dueDateSince: startOfUtcDay(nowMs - 30 * DAY_MS),
            dueDateUntil: endOfUtcDay(nowMs - 1 * DAY_MS)
        });
    }
    if (segmentId === 'days31to60') {
        return _object_spread_props(_object_spread({}, overdue), {
            dueDateSince: startOfUtcDay(nowMs - 60 * DAY_MS),
            dueDateUntil: endOfUtcDay(nowMs - 31 * DAY_MS)
        });
    }
    if (segmentId === 'days61to90') {
        return _object_spread_props(_object_spread({}, overdue), {
            dueDateSince: startOfUtcDay(nowMs - 90 * DAY_MS),
            dueDateUntil: endOfUtcDay(nowMs - 61 * DAY_MS)
        });
    }
    if (segmentId === 'days90plus') {
        return _object_spread_props(_object_spread({}, overdue), {
            dueDateUntil: endOfUtcDay(nowMs - 91 * DAY_MS)
        });
    }
    return null;
};
export const filterForAnchorSegment = (segmentId)=>{
    if (segmentId === 'anchorPaid') {
        return {
            displayStatusV2: PAID_ONLY,
            paymentDetails: [
                PaymentDetailType.PaidOnDate
            ]
        };
    }
    if (segmentId === 'externalPaid') {
        return {
            displayStatusV2: PAID_ONLY,
            paymentDetails: [
                PaymentDetailType.PaidExternally
            ]
        };
    }
    return null;
};
const expectedFilterForActive = (cardId, segmentId, now)=>{
    if (segmentId == null) return filterForCard(cardId);
    if (cardId === 'outstanding') return filterForOutstandingSegment(segmentId);
    if (cardId === 'aging-breakdown') return filterForAgingSegment(segmentId, now);
    if (cardId === 'anchor-vs-external') return filterForAnchorSegment(segmentId);
    return null;
};
const sameSet = (a, b)=>a.length === b.length && new Set(a).size === new Set([
        ...a,
        ...b
    ]).size;
export const currentFiltersMatchActiveCard = (activeCardId, activeSegmentId, currentFilters, now)=>{
    var _expected_paymentDetails, _expected_dueDateSince, _currentFilters_dueDateSince, _expected_dueDateUntil, _currentFilters_dueDateUntil;
    if (activeCardId == null) return true;
    const expected = expectedFilterForActive(activeCardId, activeSegmentId, now);
    if (expected == null) return false;
    if (!sameSet(expected.displayStatusV2, currentFilters.displayStatusV2)) return false;
    if (!sameSet((_expected_paymentDetails = expected.paymentDetails) !== null && _expected_paymentDetails !== void 0 ? _expected_paymentDetails : [], currentFilters.paymentDetails)) return false;
    return ((_expected_dueDateSince = expected.dueDateSince) !== null && _expected_dueDateSince !== void 0 ? _expected_dueDateSince : null) === ((_currentFilters_dueDateSince = currentFilters.dueDateSince) !== null && _currentFilters_dueDateSince !== void 0 ? _currentFilters_dueDateSince : null) && ((_expected_dueDateUntil = expected.dueDateUntil) !== null && _expected_dueDateUntil !== void 0 ? _expected_dueDateUntil : null) === ((_currentFilters_dueDateUntil = currentFilters.dueDateUntil) !== null && _currentFilters_dueDateUntil !== void 0 ? _currentFilters_dueDateUntil : null);
};
